import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Type, Camera, Mic, Send, Loader2, Check, ChevronDown, Video } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter186: QuickCapture's own floating FAB was removed because the
// floating-button bottom-right corner now hosts SupportChatFab +
// QuickActionsPanel. The QuickActionsPanel triggers a custom DOM event
// (`istudypro:quickcapture:open`) which we listen for here to open the
// modal — so the *feature* stays accessible, just from one consolidated
// launcher instead of a competing FAB.
const OPEN_EVENT = 'istudypro:quickcapture:open';

const QuickCapture = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState(null); // null, 'text', 'photo', 'voice'
  const [content, setContent] = useState('');
  const [subject, setSubject] = useState('Quick Captures');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const onOpen = () => setIsOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const reset = () => {
    setMode(null);
    setContent('');
    setResult(null);
    setSaving(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(reset, 300);
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/study-intelligence/quick-capture`, {
        type: mode || 'text',
        content: content.trim(),
        subject,
      });
      setResult(data);
      toast.success(`Captured! ${data.flashcards_created} flashcards created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Capture failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // For photos, we read as text placeholder (OCR would need backend processing)
    const reader = new FileReader();
    reader.onload = () => {
      setContent(`[Image: ${file.name}] — Add your notes about this image here`);
      setMode('photo');
    };
    reader.readAsDataURL(file);
  };

  const handleVoiceCapture = () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone not available');
      return;
    }
    setMode('voice');
    setContent('');
    // Start speech recognition if available
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.onresult = (event) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setContent(transcript);
      };
      recognition.onerror = () => toast.error('Voice recognition failed');
      recognition.start();
      // Auto-stop after 30 seconds
      setTimeout(() => { try { recognition.stop(); } catch {} }, 30000);
      toast.info('Listening... speak now (30s max)');
    } else {
      toast.info('Type your voice notes below');
    }
  };

  return (
    <>
      {/* FAB removed in iter186 — Quick Capture is now opened from the
          QuickActionsPanel ("Quick Capture" entry → dispatches
          `istudypro:quickcapture:open`). Modal only renders below. */}

      {/* Capture Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            className="fixed bottom-6 right-6 z-[80] w-[360px] max-h-[480px]  bg-[#141d30] border border-white/10 shadow-2xl shadow-black/50 overflow-hidden"
            data-testid="quick-capture-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h3 className="text-base font-bold text-white">Quick Capture</h3>
              <button aria-label="Close" onClick={handleClose} className="p-1 hover:bg-white/10 rounded-lg">
                <X className="w-4 h-4 text-white/50" />
              </button>
            </div>

            {/* Success State */}
            {result ? (
              <div className="p-6 text-center">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                    <Check className="w-7 h-7 text-emerald-400" />
                  </div>
                </motion.div>
                <p className="text-lg font-bold text-white mb-1">Captured!</p>
                <p className="text-sm text-white/50 mb-4">{result.flashcards_created} flashcards created from your notes</p>
                <div className="flex gap-2">
                  <Button onClick={reset} variant="outline" className="flex-1 rounded-xl border-white/10 text-white/70 text-sm">Capture More</Button>
                  <Button onClick={handleClose} className="flex-1 rounded-xl bg-[#002FA7] text-sm">Done</Button>
                </div>
              </div>
            ) : !mode ? (
              /* Mode Selection */
              <div className="p-5 space-y-3">
                <p className="text-xs text-white/40 mb-2">What would you like to capture?</p>
                <button onClick={() => setMode('text')} className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-colors text-left" data-testid="capture-text-btn">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center"><Type className="w-5 h-5 text-blue-400" /></div>
                  <div><p className="text-sm font-semibold text-white">Quick Note</p><p className="text-xs text-white/40">Paste or type study notes</p></div>
                </button>
                <button onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-colors text-left" data-testid="capture-photo-btn">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center"><Camera className="w-5 h-5 text-purple-400" /></div>
                  <div><p className="text-sm font-semibold text-white">Photo / Screenshot</p><p className="text-xs text-white/40">Capture whiteboard or textbook</p></div>
                </button>
                <button onClick={handleVoiceCapture} className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-colors text-left" data-testid="capture-voice-btn">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center"><Mic className="w-5 h-5 text-emerald-400" /></div>
                  <div><p className="text-sm font-semibold text-white">Voice Note</p><p className="text-xs text-white/40">Speak and auto-transcribe</p></div>
                </button>
                <button
                  onClick={() => { handleClose(); navigate('/study-rooms'); }}
                  className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-colors text-left"
                  data-testid="capture-video-call-btn"
                >
                  <div className="w-10 h-10 rounded-xl bg-rose-500/15 flex items-center justify-center"><Video className="w-5 h-5 text-rose-400" /></div>
                  <div><p className="text-sm font-semibold text-white">Join Video Call</p><p className="text-xs text-white/40">Hop into a study-room call</p></div>
                </button>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} className="hidden" />
              </div>
            ) : (
              /* Input Area */
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <button onClick={reset} className="text-xs text-white/40 hover:text-white flex items-center gap-1">
                    <ChevronDown className="w-3 h-3 rotate-90" /> Back
                  </button>
                  <span className="text-xs text-white/30 capitalize">{mode} capture</span>
                </div>
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder={mode === 'voice' ? 'Transcribing... or type here' : 'Paste your study notes, key concepts, or definitions...'}
                  className="w-full h-36 bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-white/25 resize-none focus:outline-none focus:border-[#002FA7]/50"
                  autoFocus
                  data-testid="capture-textarea"
                />
                <input
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Subject (e.g., Biology)"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#002FA7]/50"
                  data-testid="capture-subject"
                />
                <Button
                  onClick={handleSubmit}
                  disabled={saving || !content.trim()}
                  className="w-full rounded-xl bg-[#002FA7] hover:bg-[#002FA7]/80 gap-2"
                  data-testid="capture-submit-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {saving ? 'Processing...' : 'Capture & Create Flashcards'}
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuickCapture;
